import { AnalyticsModel } from '@/http/models/analytics'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiErrorMessage } from '@/lib/apiError'

import type { AnalyticsDataName, AnalyticsQuery, AnalyticsSignal, AnalyticsSignals } from '@/types/analytics'

/** One storefront analytics view for a date range and filters; keeps the last result while the next loads. */
export const useStoreAnalytics = <T,>(name: AnalyticsDataName, query: AnalyticsQuery & Record<string, unknown>) =>
  useQuery({
    queryKey: ['analytics', name, query],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const res = await AnalyticsModel.getData<T>(name, query)
      if (!res.success || !res.data) throw new Error(apiErrorMessage(res, 'Could not load analytics'))
      return res.data.result
    },
  })

const SIGNALS_KEY = 'analytics-signals'

/** The store's current signals (changes worth a look), most serious first. */
export const useAnalyticsSignals = () =>
  useQuery({
    queryKey: [SIGNALS_KEY],
    // Staff without analytics access get 403: no point retrying.
    retry: false,
    queryFn: async (): Promise<AnalyticsSignals> => {
      const res = await AnalyticsModel.getSignals()
      if (!res.success || !res.data) throw new Error(apiErrorMessage(res, 'Could not load signals'))
      return res.data
    },
  })

/** Marks a signal seen or dismissed, or records whether it was useful. */
export const useUpdateAnalyticsSignal = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...body }: { id: string; status?: AnalyticsSignal['status']; feedback?: AnalyticsSignal['feedback'] }) => {
      const res = await AnalyticsModel.updateSignal(id, body)
      if (!res.success) throw new Error(apiErrorMessage(res, 'Could not update the signal'))
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [SIGNALS_KEY] }),
  })
}
